// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Væg tool (frontend-u0o.4, slice S4; design.md §4, mock 02 — Revit's
 * wall tool with its Options Bar, SketchUp's inference): what a drawn
 * chain, rectangle or plane trace becomes as `POST /model/edits` ops.
 *
 *  - The options (the strip above the dock): Linje / Rektangel / Fra
 *    planspor, thickness, location line (Midte / Inde / Ude — a UI-only
 *    offset of the axis from the drawn line), height (to the storey's
 *    ceiling, or typed) and Kæde (chain on: each click goes on from the
 *    last point; off: every two clicks make one wall).
 *  - Location line: the drawn line is the axis (Midte), the interior face
 *    (Inde) or the exterior face (Ude). An open chain follows Revit's
 *    clockwise convention (the exterior lies left of the drawing
 *    direction); a closed loop knows its inside from its winding. The axis
 *    polyline is the drawn one offset by half the thickness, mitred at the
 *    corners.
 *  - Auto-join: a chain that starts or ends on an existing wall joins it —
 *    at that wall's end a corner (both ends move to where the axes meet),
 *    along its body a tee (the new wall ends on its axis). Consecutive
 *    walls of the chain are corners; a closed loop joins its last wall to
 *    its first. The joins ride along on the create ops (`joins`), the
 *    existing wall's moved end as a `wall.update`.
 *
 * One drawn chain is one batch, so one undo. World x/y. Pure: no React, no
 * DOM.
 */

import type { ModelEditOp, SolidWall, Storey } from '../api/types';
import { lineIntersection, nearestOnSegment } from './inference';
import type { XY } from './planWalls';

export type WallMode = 'line' | 'rectangle' | 'trace';
export type Placement = 'center' | 'inside' | 'outside';

export interface WallDrawOptions {
  mode: WallMode;
  /** Metres. */
  thickness: number;
  /** Metres above the storey floor; null = up to the storey's ceiling (Til loft). */
  height: number | null;
  placement: Placement;
  /** Kæde: each click goes on from the last point. */
  chain: boolean;
}

export const DEFAULT_WALL_OPTIONS: WallDrawOptions = { mode: 'line', thickness: 0.12, height: null, placement: 'center', chain: true };

/** A wall's height when the storey is unknown (metres). */
export const FALLBACK_HEIGHT = 2.7;

/** Shorter than this (metres) is no wall. */
export const MIN_WALL = 0.05;

const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const add = (a: XY, b: XY): XY => [a[0] + b[0], a[1] + b[1]];
const mul = (a: XY, k: number): XY => [a[0] * k, a[1] * k];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const crs = (a: XY, b: XY) => a[0] * b[1] - a[1] * b[0];
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const r6 = (v: number) => Math.round(v * 1e6) / 1e6 + 0;
const pt = (p: XY): [number, number] => [r6(p[0]), r6(p[1])];

function unit(v: XY): XY | null {
  const L = Math.hypot(v[0], v[1]);
  return L > 1e-9 ? [v[0] / L, v[1] / L] : null;
}

/** Twice the signed area of a ring (> 0 counter-clockwise). */
export function signedArea2(ring: readonly XY[]): number {
  let s = 0;
  for (let i = 0; i < ring.length; i += 1) s += crs(ring[i], ring[(i + 1) % ring.length]);
  return s;
}

/**
 * How far the axis lies from the drawn line, along the left normal of
 * the drawing direction: 0 for Midte; ±half the thickness for Inde / Ude.
 * Open: the exterior is on the left (Revit draws clockwise), so Ude (the
 * line is the exterior face) puts the body — and the axis — to the right.
 * Closed: the interior is on the left of a counter-clockwise loop.
 */
export function placementOffset(placement: Placement, thickness: number, closedWinding: 'ccw' | 'cw' | null = null): number {
  if (placement === 'center') return 0;
  const half = thickness / 2;
  // Which side the interior lies on: right for an open chain (clockwise), else by winding.
  const interiorLeft = closedWinding === 'ccw';
  // Ude: the body lies on the interior side; Inde: on the exterior side.
  const bodyLeft = placement === 'outside' ? interiorLeft : !interiorLeft;
  return bodyLeft ? half : -half;
}

/**
 * The polyline `points` offset by `offset` along each segment's left
 * normal, mitred at the corners (adjacent offset lines intersected); a
 * closed ring is mitred at its first point too. Collinear corners keep the
 * plain offset.
 */
export function offsetPolyline(points: readonly XY[], offset: number, closed: boolean): XY[] {
  const n = points.length;
  if (n < 2 || offset === 0) return points.map((p) => [p[0], p[1]] as XY);
  const segs: { a: XY; d: XY; nrm: XY }[] = [];
  const count = closed ? n : n - 1;
  for (let i = 0; i < count; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % n];
    const d = unit(sub(b, a)) ?? [1, 0];
    const nrm: XY = [-d[1], d[0]];
    segs.push({ a: add(a, mul(nrm, offset)), d, nrm });
  }
  const out: XY[] = [];
  for (let i = 0; i < n; i += 1) {
    const prev = closed ? segs[(i - 1 + count) % count] : (segs[i - 1] ?? null);
    const next = closed ? segs[i % count] : (segs[i] ?? null);
    const p = points[i];
    if (prev && next) {
      const x = lineIntersection(prev.a, prev.d, next.a, next.d);
      out.push(x ?? add(p, mul(next.nrm, offset)));
    } else {
      const s = (next ?? prev)!;
      out.push(add(p, mul(s.nrm, offset)));
    }
  }
  return out;
}

/** A drawn point resting on an existing wall: which, and where along it. */
export interface WallHit {
  wall: SolidWall;
  /** 0..1 along its axis. */
  t: number;
}

/** Slack beyond half the thickness (metres) for a point to count as on a wall. */
const ON_SLACK = 0.02;

/**
 * The wall of `walls` (on `storeyId`, leaving out `exclude`) whose body holds `p`, nearest its axis.
 * `slack` (metres) widens every wall: a click's pixel reach — a wall of unknown thickness is a
 * line, which a click would otherwise have to hit within 2 cm.
 */
export function wallAt(walls: readonly SolidWall[], p: XY, storeyId: number | null, exclude: ReadonlySet<string> = new Set(), slack = 0): WallHit | null {
  let best: (WallHit & { d: number }) | null = null;
  for (const w of walls) {
    if (exclude.has(w.guid) || (storeyId !== null && w.storey_id !== storeyId)) continue;
    const { point, t } = nearestOnSegment(w.start, w.end, p);
    const d = dist(point, p);
    if (d > (w.thickness ?? 0) / 2 + ON_SLACK + slack) continue;
    if (!best || d < best.d) best = { wall: w, t, d };
  }
  return best ? { wall: best.wall, t: best.t } : null;
}

/** How the end of a new wall meets an existing one. */
export interface EndJoin {
  /** Where the new wall's axis ends. */
  at: XY;
  /** The existing wall's end that moves there (a corner), or null (a tee: it stays). */
  moves: 'start' | 'end' | null;
  kind: 'corner' | 'tee';
  wall: SolidWall;
}

/** Below this sine (~3°) a new wall runs along the one it touches: no join. */
const MIN_SINE = Math.sin((3 * Math.PI) / 180);

/**
 * Where a new axis from `from` towards `p` meets the wall it touches at
 * `p`: the axes' intersection. Near that wall's end (within its and the new
 * wall's half thickness, plus slack) a corner — the wall's end moves to the
 * intersection; along its body a tee. Null when the two run parallel.
 */
export function endJoin(hit: WallHit, from: XY, p: XY, thickness: number): EndJoin | null {
  const w = hit.wall;
  const wd = sub(w.end, w.start);
  const L = Math.hypot(wd[0], wd[1]);
  const nd = unit(sub(p, from));
  if (!(L > 1e-9) || !nd) return null;
  if (Math.abs(crs(nd, mul(wd, 1 / L))) < MIN_SINE) return null;
  const x = lineIntersection(from, nd, w.start, wd);
  if (!x) return null;
  const s = dot(sub(x, w.start), wd) / L;
  const reach = Math.max(w.thickness ?? 0, thickness) / 2 + ON_SLACK;
  if (s <= reach || s >= L - reach) {
    return { at: x, moves: s <= L / 2 ? 'start' : 'end', kind: 'corner', wall: w };
  }
  return { at: x, moves: null, kind: 'tee', wall: w };
}

export interface DrawContext {
  walls: readonly SolidWall[];
  storey: Storey | null;
  storeyId: number | null;
  /** Fresh guids for the new walls (the joins name them). */
  newGuid: () => string;
}

/** A wall the tool will create, its axis world x/y. */
export interface PlannedWall {
  guid: string;
  start: XY;
  end: XY;
}

export interface ChainPlan {
  walls: PlannedWall[];
  ops: ModelEditOp[];
}

/** The heights of a new wall on `storey` (world z). */
export function wallHeights(storey: Storey | null, height: number | null): { z_base: number; z_top: number } {
  const z0 = storey?.z_min ?? 0;
  const top = height !== null ? z0 + height : storey && storey.z_max > z0 + 0.1 ? storey.z_max : z0 + FALLBACK_HEIGHT;
  return { z_base: r6(z0), z_top: r6(top) };
}

/** Whether the drawn points close on themselves (the last on the first), with at least three corners. */
export function isClosed(points: readonly XY[], tol = 1e-4): boolean {
  return points.length >= 4 && dist(points[0], points[points.length - 1]) <= tol;
}

/**
 * The ops for a drawn chain `points` (clicked, in order; a closed one ends
 * on its first point): one `wall.create` per segment on the axis the
 * location line gives, corners between consecutive walls, and the chain's
 * open ends joined to the walls they rest on. Segments shorter than
 * `MIN_WALL` are dropped. Empty when nothing is left to draw.
 */
export function chainPlan(drawn: readonly XY[], options: WallDrawOptions, ctx: DrawContext): ChainPlan {
  // Drop repeated clicks.
  const pts: XY[] = [];
  for (const p of drawn) if (pts.length === 0 || dist(pts[pts.length - 1], p) >= MIN_WALL) pts.push(p);
  const closed = isClosed(pts, MIN_WALL);
  const ring = closed ? pts.slice(0, -1) : pts;
  if (ring.length < 2) return { walls: [], ops: [] };
  const winding = closed ? (signedArea2(ring) > 0 ? 'ccw' : 'cw') : null;
  const axis = offsetPolyline(ring, placementOffset(options.placement, options.thickness, winding), closed);
  if (closed) axis.push(axis[0]);

  const walls: PlannedWall[] = [];
  for (let i = 0; i + 1 < axis.length; i += 1) walls.push({ guid: ctx.newGuid(), start: axis[i], end: axis[i + 1] });

  const extra: ModelEditOp[] = [];
  const joins = new Map<string, Record<string, unknown>[]>(walls.map((w) => [w.guid, []]));
  if (!closed) {
    // The open ends: joined to the wall the drawn point rests on.
    const ends: [number, 'start' | 'end', XY][] = [
      [0, 'start', ring[0]],
      [walls.length - 1, 'end', ring[ring.length - 1]],
    ];
    for (const [i, end, drawnAt] of ends) {
      const w = walls[i];
      const hit = wallAt(ctx.walls, drawnAt, ctx.storeyId);
      if (!hit) continue;
      const other = end === 'start' ? w.end : w.start;
      const j = endJoin(hit, other, end === 'start' ? w.start : w.end, options.thickness);
      if (!j) continue;
      w[end] = j.at;
      if (j.moves) extra.push({ op: 'wall.update', guid: j.wall.guid, set: { [j.moves]: pt(j.at) } });
      joins.get(w.guid)!.push({ wall_a: j.wall.guid, wall_b: w.guid, kind: j.kind, at_a: j.moves ?? 'path', at_b: end });
    }
  }
  for (let i = 1; i < walls.length; i += 1) {
    joins.get(walls[i].guid)!.push(cornerJoin(walls[i - 1], walls[i]));
  }
  if (closed && walls.length > 2) joins.get(walls[walls.length - 1].guid)!.push(cornerJoin(walls[walls.length - 1], walls[0]));

  const kept = walls.filter((w) => dist(w.start, w.end) >= MIN_WALL);
  const keptGuids = new Set(kept.map((w) => w.guid));
  const planned = new Set(walls.map((w) => w.guid));
  // A join stands when both its walls do (an existing one, or a kept new one).
  const stands = (g: unknown) => typeof g === 'string' && (!planned.has(g) || keptGuids.has(g));
  const { z_base, z_top } = wallHeights(ctx.storey, options.height);
  const ops: ModelEditOp[] = [];
  for (const w of kept) {
    const js = joins.get(w.guid)!.filter((j) => stands(j.wall_a) && stands(j.wall_b));
    ops.push({
      op: 'wall.create',
      record: {
        guid: w.guid,
        storey_id: ctx.storey?.id ?? ctx.storeyId,
        start: pt(w.start),
        end: pt(w.end),
        z_base,
        z_top,
        height: r6(z_top - z_base),
        thickness: r6(options.thickness),
        is_external: false,
        assumed: false,
      },
      ...(js.length > 0 ? { joins: js } : {}),
    });
  }
  return { walls: kept, ops: kept.length > 0 ? [...extra, ...ops] : [] };
}

/** The join between consecutive walls `a` (its end) and `b` (its start): a corner, or collinear when they go straight on. */
function cornerJoin(a: PlannedWall, b: PlannedWall): Record<string, unknown> {
  const da = unit(sub(a.end, a.start));
  const db = unit(sub(b.end, b.start));
  const straight = da && db ? Math.abs(crs(da, db)) < MIN_SINE && dot(da, db) > 0 : false;
  return { wall_a: a.guid, wall_b: b.guid, kind: straight ? 'collinear' : 'corner', at_a: 'end', at_b: 'start' };
}

/**
 * Rektangel: the four corners from two opposite ones, in the storey's
 * Manhattan frame (`axisAngle`, radians), counter-clockwise from `a` and
 * closed (the first repeated). Null when it would have no area.
 */
export function rectanglePoints(a: XY, c: XY, axisAngle: number): XY[] | null {
  const ux: XY = [Math.cos(axisAngle), Math.sin(axisAngle)];
  const uy: XY = [-ux[1], ux[0]];
  const v = sub(c, a);
  const w = dot(v, ux);
  const h = dot(v, uy);
  if (Math.abs(w) < MIN_WALL || Math.abs(h) < MIN_WALL) return null;
  const b = add(a, mul(ux, w));
  const d = add(a, mul(uy, h));
  const ring: XY[] = [a, b, c, d];
  if (signedArea2(ring) < 0) ring.reverse();
  return [...ring, ring[0]];
}

/**
 * Fra planspor: the axis of a wall on a detected plane's trace `a`–`b`,
 * which is a face — the body goes to the side `toward` lies on (with Midte
 * the trace is the axis). Its ends reach to the walls they nearly meet (a
 * trace stops short of the corners: within `reach` metres either way an
 * end goes onto that wall's axis, so `chainPlan` joins it there). Draw it
 * with placement Midte: the offset is already in.
 */
export function traceAxis(a: XY, b: XY, toward: XY, options: WallDrawOptions, walls: readonly SolidWall[], storeyId: number | null, reach = 0.6): XY[] | null {
  const d = unit(sub(b, a));
  if (!d) return null;
  const nrm: XY = [-d[1], d[0]];
  const side = dot(sub(toward, a), nrm) >= 0 ? 1 : -1;
  const off = options.placement === 'center' ? 0 : (side * options.thickness) / 2;
  const p0 = add(a, mul(nrm, off));
  const p1 = add(b, mul(nrm, off));
  const L = dist(p0, p1);
  // The nearest wall axis the line crosses beyond (or just inside) an end.
  const reachOut = (from: XY, dir: XY): XY => {
    let best: { x: XY; s: number } | null = null;
    for (const w of walls) {
      if (storeyId !== null && w.storey_id !== storeyId) continue;
      const wd = sub(w.end, w.start);
      const wl = Math.hypot(wd[0], wd[1]);
      if (!(wl > 1e-9) || Math.abs(crs(dir, mul(wd, 1 / wl))) < MIN_SINE) continue;
      const x = lineIntersection(from, dir, w.start, wd);
      if (!x) continue;
      const along = nearestOnSegment(w.start, w.end, x);
      if (dist(along.point, x) > (w.thickness ?? 0) / 2 + ON_SLACK) continue;
      const s = dot(sub(x, from), dir);
      if (s > reach || s < -Math.min(reach, L / 2)) continue;
      if (!best || Math.abs(s) < Math.abs(best.s)) best = { x, s };
    }
    return best?.x ?? from;
  };
  return [reachOut(p0, mul(d, -1)), reachOut(p1, d)];
}
